/**
 * Validación de `offline_data` en el spec_json de un bot offline (v2).
 *
 * `offline_data` le indica al cliente Android qué descargar al pulsar "Sincronizar" y en qué
 * variables de sesión dejarlo al iniciar cada conversación (el motor BotCore lo ignora):
 *
 *   "offline_data": {
 *     "request": { "method": "GET", "url": "https://…", "params": {}, "headers": {}, "timeout": 60000 },
 *     "assign": [ { "map": "personal", "to": "variables.session.personal" } ]
 *   }
 *
 * `assign` usa el mismo formato que map_result: `map` es una ruta con puntos en la respuesta y
 * `to` una variable de sesión declarada en el spec.
 */

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const SESSION_TARGET = /^variables\.session\.([A-Za-z_$][\w$]*)$/;

/** @param {unknown} value */
const isPlainObject = (value) => !!value && typeof value === 'object' && !Array.isArray(value);

/**
 * @param {unknown} value
 * @param {string} field
 * @returns {string[]}
 */
const validatePrimitiveMap = (value, field) => {
  if (value === undefined) return [];
  if (!isPlainObject(value)) return [`offline_data.request.${field} debe ser un objeto`];
  return Object.entries(value)
    .filter(([, v]) => v !== null && typeof v === 'object')
    .map(([k]) => `offline_data.request.${field}.${k} debe ser texto o número`);
};

/**
 * @param {Record<string, any>} request
 * @returns {string[]}
 */
const validateRequest = (request) => {
  if (!isPlainObject(request)) return ['offline_data.request es obligatorio y debe ser un objeto'];

  const errors = [];
  const { url, method, timeout } = request;
  if (typeof url !== 'string' || !/^https?:\/\/\S+$/.test(url.trim())) {
    errors.push('offline_data.request.url debe ser una URL http(s)');
  }
  if (method !== undefined && !METHODS.includes(String(method).toUpperCase())) {
    errors.push(`offline_data.request.method debe ser uno de: ${METHODS.join(', ')}`);
  }
  if (timeout !== undefined && !(typeof timeout === 'number' && timeout > 0)) {
    errors.push('offline_data.request.timeout debe ser un número mayor que 0 (milisegundos)');
  }
  return [
    ...errors,
    ...validatePrimitiveMap(request.params, 'params'),
    ...validatePrimitiveMap(request.headers, 'headers'),
  ];
};

/**
 * @param {unknown} assign
 * @param {Record<string, any>} sessionVariables
 * @returns {string[]}
 */
const validateAssign = (assign, sessionVariables) => {
  if (!Array.isArray(assign) || assign.length === 0) {
    return ['offline_data.assign debe ser una lista con al menos un elemento { map, to }'];
  }

  /** @type {string[]} */
  const errors = [];
  const seen = new Set();
  assign.forEach((item, index) => {
    const where = `offline_data.assign[${index}]`;
    if (!isPlainObject(item)) {
      errors.push(`${where} debe ser un objeto { map, to }`);
      return;
    }
    if (typeof item.map !== 'string' || item.map.trim() === '') {
      errors.push(`${where}.map debe ser la ruta del dato en la respuesta (ej. "personal")`);
    }
    const match = typeof item.to === 'string' ? item.to.trim().match(SESSION_TARGET) : null;
    if (!match) {
      errors.push(`${where}.to debe tener la forma "variables.session.<nombre>"`);
      return;
    }
    const [, name] = match;
    if (!Object.prototype.hasOwnProperty.call(sessionVariables, name)) {
      errors.push(`${where}.to: la variable de sesión "${name}" no está declarada en variables.session`);
    }
    if (seen.has(name)) {
      errors.push(`${where}.to: la variable "${name}" se asigna más de una vez`);
    }
    seen.add(name);
  });
  return errors;
};

/**
 * Devuelve la lista de errores de `offline_data` (vacía si es válido o si el spec no lo tiene).
 *
 * @param {Record<string, any>} spec - spec_json ya parseado
 * @returns {string[]}
 */
export const validateOfflineData = (spec) => {
  if (!isPlainObject(spec) || spec.offline_data === undefined) return [];

  const offlineData = spec.offline_data;
  if (!isPlainObject(offlineData)) return ['offline_data debe ser un objeto { request, assign }'];

  const errors = [];
  if (spec.version !== '2.0') {
    errors.push('offline_data solo aplica a bots offline (version "2.0")');
  }
  const sessionVariables = isPlainObject(spec.variables?.session) ? spec.variables.session : {};
  return [
    ...errors,
    ...validateRequest(offlineData.request),
    ...validateAssign(offlineData.assign, sessionVariables),
  ];
};

export default validateOfflineData;
